import React, {
  useState,
  useEffect
} from 'react'
import {
  useDispatch,
  useSelector
} from 'react-redux'
import {
  useNavigate,
  Link as RouterLink
} from 'react-router-dom'

import { 
  LockOutlined as LockOutlinedIcon,
  CheckCircle as CheckCircleIcon 
} from '@mui/icons-material'

import {
  Alert,
  Box,
  Typography,
  Container,
  Avatar,
  TextField,
  FormControlLabel,
  Checkbox,
  Button,
  Grid,
  Link,
} from '@mui/material'

import NoticeBox from './NoticeBox'

import ReCAPTCHA from 'react-google-recaptcha'

import {
  clearFlashAlert,
  clearFlashNotice,
} from '../../redux/features/ui/uiSlice'
import { selectCurrentUser } from '../../redux/features/user/userSlice'

const classes = {
  link: {
    margin: '0 5px 0 5px',
    cursor: 'pointer',
  }
}

export default function ConfirmationInstructions() {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  // 0.6.27.13: this screen is also where a no-login OpenID claim lands,
  // navigated to straight from MigrateOpenidAccount.jsx's own successful
  // submit, with currentUser optimistically still set from that request
  // (#claim actually signs the account out server-side as soon as it
  // has a login -- a reload of this exact screen would show it as
  // signed out again, same as any other confirmation-pending visitor).
  // While it's set, though, "Sign in" makes no sense (already signed
  // in), and "resend" means going back to fix whatever was wrong in the
  // claim form (a mistyped email, most likely) by re-authenticating at
  // the OpenID provider, not the unauthenticated resend_confirmation
  // flow this account can't use to fix that anyway.
  //
  // 0.6.27.16: a version-1 verification (VerifyAccount.jsx) lands here
  // signed in too, the same way a claim does, but "fix it" has to go
  // back to a different screen -- distinguish the two by
  // `openid_identifier`, which only an OpenidUser with an identity has
  // (same signal MigrateOpenidAccount.jsx already uses to show which
  // identity it's migrating), rather than by `currentUser` alone.
  const currentUser = useSelector(selectCurrentUser)
  const isClaiming = Boolean(currentUser?.openid_identifier)
  const fixItPath = isClaiming ? '/app/migrate_openid_account' : '/app/verify_account'

  const [recaptchaResponse, setRecaptchaResponse] = useState('')
  const [authToken, setAuthToken] = useState('')

  const handleLinkClick = (e) => {
    dispatch(clearFlashNotice())
    dispatch(clearFlashAlert())
  }

  return (
    <React.Fragment>
      <Container component="main" maxWidth="md">
        <Box
          sx={{
            marginTop: 9,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Avatar sx={{ m: 1, bgcolor: 'secondary.main' }}>
            <CheckCircleIcon />
          </Avatar>

          <Typography component='h1' variant='h5'>
            Confirm your email
          </Typography>
          <Box sx={{ width: '100%', maxWidth: 400 }}>
            <NoticeBox>
              <strong>Almost there!</strong>
              <p>                
                You now need to confirm your email.
              </p>
              <p>
                Check your inbox and click the link we sent. 
              </p>
              <p>
                After that, you'll be able to log in.
              </p>
            </NoticeBox>
            <Grid container>
              <Grid sx={{ flexGrow: 1 }}>
                <Link
                  component={RouterLink}
                  onClick={handleLinkClick}
                  to={currentUser ? fixItPath : '/app/resend_confirmation'}
                  sx={classes.link}
                  rel='nofollow'
                  variant='body2'
                >
                  {currentUser ? 'Got the wrong email? Fix it' : 'Resend confirmation link'}
                </Link>
              </Grid>
              <Grid sx={{ display: currentUser ? 'none' : undefined }}>
                <Link
                  component={RouterLink}
                  onClick={handleLinkClick}
                  to='/app/sign_in'
                  sx={classes.link}
                  rel='nofollow'
                  variant='body2'
                >
                  Already have an account? Sign In
                </Link>
              </Grid>
            </Grid>            
          </Box>
        </Box>
      </Container>
    </React.Fragment>
  )
}

